import { FC, Fragment, useEffect, useState } from "react";
import { BaseEvent } from "@/app/events/events";
import { useParams } from "react-router-dom";
import { DateTime, Interval } from "luxon";
import { t } from "i18next";
import Color from "color";

import { colors } from "@/app/fees/fees";
import { Calendar, ChevronRight, Clock, X } from "lucide-react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const groupBy = (array, key) => {
  if (!array) return {}; // Asegúrate de que el array esté definido
  return array.reduce((result, currentValue) => {
    (result[currentValue[key]] = result[currentValue[key]] || []).push(
      currentValue
    );
    return result;
  }, {});
};

export type CalendarMonthProps = {
  currentDateTime: DateTime;
  onEventClick?: (event: BaseEvent) => void;
  events?: Array<any>;
};

export type CalendarMonthItem = {
  isToday: boolean;
  date: DateTime;
  events: any;
  isCurrentMonth: boolean;
  key: string;
  isWeekend: any;
  isSelected: any;
};

export const CalendarMonth: FC<CalendarMonthProps> = ({
  onEventClick,
  currentDateTime,
  events,
}) => {
  const { id } = useParams();

  const [items, setItems] = useState<Record<string, any>>({});
  const [days, setDays] = useState([]);

  useEffect(() => {
    setItems(() => groupBy(events, "date"));
  }, [events]);

  useEffect(() => {
    if (!currentDateTime) {
      return;
    }

    const startOfMonth = currentDateTime.startOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfMonth = currentDateTime.endOf("month");
    const endOfWeek = endOfMonth.endOf("week");

    const today = DateTime.now().startOf("day");
    const todayFormatted = today.toFormat("yyyy-MM-dd");

    const interval = Interval.fromDateTimes(startOfWeek, endOfWeek);
    const days = interval.splitBy({ day: 1 }).map((interval) => {
      const dayFormatted = interval.start
        ?.startOf("day")
        .toFormat("yyyy-MM-dd");
      const isToday = dayFormatted === todayFormatted;

      return {
        date: interval.start,
        key: dayFormatted,
        isToday,
        isCurrentMonth: interval.start?.hasSame(currentDateTime, "month"),
        isWeekend: interval.start?.isWeekend,
        events: [],
      };
    });

    setDays(days);
  }, [currentDateTime]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEvents, setModalEvents] = useState([]);

  const openModal = (events: []) => {
    setModalEvents(events);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalEvents([]);
  };

  return (
    <Fragment>
      <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col flex-1 w-full overflow-auto">
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
          <div className="bg-white py-2">{t("days.monday")}</div>
          <div className="bg-white py-2">{t("days.tuesday")}</div>
          <div className="bg-white py-2">{t("days.wednesday")}</div>
          <div className="bg-white py-2">{t("days.thursday")}</div>
          <div className="bg-white py-2">{t("days.friday")}</div>
          <div className="bg-white py-2">{t("days.saturday")}</div>
          <div className="bg-white py-2">{t("days.sunday")}</div>
        </div>
        <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            {days.map((day) => (
              <div
                key={day.date.toISO()}
                className={classNames(
                  day.isCurrentMonth ? "bg-white" : "bg-gray-50 text-gray-500",
                  "relative px-3 py-2"
                )}
              >
                <time
                  dateTime={day.date.toISO()?.toString()}
                  className={
                    day.isToday
                      ? "flex h-6 w-6 items-center justify-center rounded-full bg-primary font-semibold text-white"
                      : undefined
                  }
                >
                  {day.date.day}
                </time>
                {items[day.key]?.length > 0 && (
                  <ol className="mt-2">
                    {items[day.key]?.slice(0, 2).map((event) => {
                      const isUser = event.users?.find(
                        (user) => user.id === Number(id)
                      )
                        ? true
                        : false;

                      return (
                        <li key={event.id} onClick={() => onEventClick(event)}>
                          <a href={event.href} className="group flex relative">
                            <p
                              style={{
                                backgroundColor: event.color
                                  ? Color(event.color).alpha(0.75).hexa()
                                  : colors[0].color,
                              }}
                              className="flex-auto truncate font-medium rounded-md px-3 py-1 mb-2 text-gray-900 group-hover:text-gray-600 cursor-pointer"
                            >
                              <span
                                style={{
                                  backgroundColor: isUser ? "#7CFC00" : "gray",
                                }}
                                className="bg-gray-950 w-1 h-1 p-1 rounded-full absolute top-3 left-2"
                              />
                              <time
                                dateTime={event.date}
                                className="ml-3 flex-none text-gray-700 group-hover:text-primary"
                              >
                                {event.startTime
                                  ? DateTime.fromFormat(
                                      event.startTime,
                                      "hh:mm:ss"
                                    ).toFormat("hh:mm")
                                  : ""}
                              </time>{" "}
                              {event.name}
                            </p>

                            <div
                              className="absolute left-1/2 -top-1 w-3/4 justify-center transform -translate-x-1/2 shadow-md -translate-y-full bg-white text-gray-800 text-xs rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity
                                            before:content-[''] before:absolute before:left-1/2 before:top-full before:-translate-x-1/2 before:border-4 before:border-transparent before:border-t-white"
                            >
                              {event.startTime
                                ? DateTime.fromFormat(
                                    event.startTime,
                                    "hh:mm:ss"
                                  ).toFormat("hh:mm")
                                : ""}
                              {" - "}
                              {event.endTime
                                ? DateTime.fromFormat(
                                    event.endTime,
                                    "hh:mm:ss"
                                  ).toFormat("hh:mm")
                                : ""}
                            </div>
                          </a>
                        </li>
                      );
                    })}
                    {items[day.key].length > 2 && (
                      <li
                        onClick={() => openModal(items[day.key])}
                        className="text-gray-500 cursor-pointer"
                      >
                        + {items[day.key].length - 2} más
                      </li>
                    )}
                  </ol>
                )}
              </div>
            ))}
          </div>
          <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
            {days.map((day) => (
              <button
                key={day.date.toISO()}
                type="button"
                className={classNames(
                  day.isCurrentMonth
                    ? day.isWeekend
                      ? "bg-gray-200"
                      : "bg-white"
                    : day.isWeekend
                      ? "bg-gray-200"
                      : "bg-gray-50",
                  (day.isSelected || day.isToday) && "font-semibold",
                  day.isSelected && "text-white",
                  !day.isSelected && day.isToday && "text-primary",
                  !day.isSelected &&
                    day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-900",
                  !day.isSelected &&
                    !day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-500",
                  "flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10"
                )}
              >
                <time
                  dateTime={day.date.toISO()}
                  className={classNames(
                    day.isSelected &&
                      "flex h-6 w-6 items-center justify-center rounded-full",
                    day.isSelected && day.isToday && "bg-primary",
                    day.isSelected && !day.isToday && "bg-gray-900",
                    "ml-auto"
                  )}
                >
                  {day.date.day}
                </time>
                <span className="sr-only">{day.events.length} eventos</span>
                {items[day.key]?.length > 0 && (
                  <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                    {items[day.key].map((event) => {
                      const isUser = event.users?.find(
                        (user) => user.id === Number(id)
                      )
                        ? true
                        : false;

                      return (
                        <span
                          key={event.id}
                          onClick={() => onEventClick(event)}
                          style={{
                            backgroundColor: isUser
                              ? "#7CFC00"
                              : Color(event.color).alpha(0.75).hexa(),
                          }}
                          className={classNames(
                            "mx-0.5 mb-1 h-4 w-4 rounded-full cursor-pointer"
                          )}
                        />
                      );
                    })}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Modal open={isModalOpen} onClose={closeModal} title="Eventos">
        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          <div className="px-2">
            {modalEvents.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {modalEvents.map((event) => (
                  <EventItem
                    key={event.id}
                    event={event}
                    onClick={(event) => {
                      onEventClick(event);
                      closeModal();
                    }}
                  />
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">
                  No hay eventos programados
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Los eventos aparecerán aquí cuando sean agregados
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

// Componente de evento individual
const EventItem = ({ event, onClick }) => {
  return (
    <li className="group">
      <button
        onClick={() => onClick(event)}
        className="w-full p-4 text-left hover:bg-gray-50 transition-all duration-200 hover:shadow-sm"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 group-hover:text-gray-600 transition-colors duration-200 truncate">
              {event.name}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <time dateTime={event.startTime} className="truncate">
                {DateTime.fromFormat(event.startTime, "HH:mm:ss").toFormat(
                  "HH:mm a"
                )}
              </time>
              {" - "}
              <time dateTime={event.endTime} className="truncate">
                {DateTime.fromFormat(event.endTime, "HH:mm:ss").toFormat(
                  "HH:mm a"
                )}
              </time>
            </div>
            {event.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {event.description}
              </p>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-500 transition-colors duration-200 flex-shrink-0 ml-3" />
        </div>
      </button>
    </li>
  );
};

const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        onClick={onClose}
      />

      {/* Modal content */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};
